export type ExamExtensionId = 'ephemeral' | 'short' | 'medium' | 'long';
export type ExamFocusId = 'conceptual' | 'learning' | 'practical' | 'inquiry';
export type ExamLevelId =
  | 'nt1'
  | 'nt2'
  | '1basico'
  | '2basico'
  | '3basico'
  | '4basico'
  | '5basico'
  | '6basico'
  | '7basico'
  | '8basico';

export interface ExamExtensionOption {
  id: ExamExtensionId;
  icon: string;
  label: string;
  rangeLabel: string;
  minBlocks: number;
  maxBlocks: number;
  exactBlocks: number;
  purpose: string;
}

export interface ExamLevelOption {
  id: ExamLevelId;
  label: string;
  constraints: string;
}

export interface ExamFocusOption {
  id: ExamFocusId;
  icon: string;
  label: string;
  description: string;
}

export const EXAM_EXTENSION_OPTIONS: ExamExtensionOption[] = [
  {
    id: 'ephemeral',
    icon: '⚡',
    label: 'Efímero',
    rangeLabel: '1–5 bloques',
    minBlocks: 1,
    maxBlocks: 5,
    exactBlocks: 4,
    purpose: 'Rompehielos o ticket de salida rápido',
  },
  {
    id: 'short',
    icon: '⏱️',
    label: 'Corto',
    rangeLabel: '6–10 bloques',
    minBlocks: 6,
    maxBlocks: 10,
    exactBlocks: 8,
    purpose: 'Quiz semanal o repaso de conceptos',
  },
  {
    id: 'medium',
    icon: '📊',
    label: 'Medio',
    rangeLabel: '11–18 bloques',
    minBlocks: 11,
    maxBlocks: 18,
    exactBlocks: 14,
    purpose: 'Evaluación parcial de unidad',
  },
  {
    id: 'long',
    icon: '🏆',
    label: 'Largo',
    rangeLabel: '19–25 bloques',
    minBlocks: 19,
    maxBlocks: 25,
    exactBlocks: 22,
    purpose: 'Examen sumativo o prueba semestral',
  },
];

export const EXAM_LEVEL_OPTIONS: ExamLevelOption[] = [
  {
    id: 'nt1',
    label: 'NT1',
    constraints: 'Educación parvularia (transición 1): juego, motricidad gruesa/fina, lenguaje oral y exploración sensorial. Sin lectoescritura formal ni operaciones aritméticas.',
  },
  {
    id: 'nt2',
    label: 'NT2',
    constraints: 'Educación parvularia (transición 2): preparación al 1° básico con juego estructurado, conciencia fonológica inicial y nociones pre-numéricas. Evita textos largos y algoritmos.',
  },
  {
    id: '1basico',
    label: '1° Básico',
    constraints: 'Operaciones hasta 20, conteo, comparación. Sin fracciones ni números negativos.',
  },
  {
    id: '2basico',
    label: '2° Básico',
    constraints: 'Suma y resta hasta 100, inicio de multiplicación simple. Vocabulario concreto.',
  },
  {
    id: '3basico',
    label: '3° Básico',
    constraints: 'Multiplicación y división básica, medidas simples. Problemas de un paso.',
  },
  {
    id: '4basico',
    label: '4° Básico',
    constraints: 'Operaciones hasta 10.000, fracciones visuales simples, perímetro básico.',
  },
  {
    id: '5basico',
    label: '5° Básico',
    constraints: 'Fracciones equivalentes, decimales introductorios. NO usar números negativos ni álgebra simbólica.',
  },
  {
    id: '6basico',
    label: '6° Básico',
    constraints: 'Razones, porcentajes simples, ecuaciones de un paso muy guiadas.',
  },
  {
    id: '7basico',
    label: '7° Básico',
    constraints: 'Números enteros, proporcionalidad, ecuaciones lineales simples, álgebra introductoria.',
  },
  {
    id: '8basico',
    label: '8° Básico',
    constraints: 'Funciones lineales básicas, sistemas simples, raíces cuadradas exactas, demostraciones cortas.',
  },
];

export const EXAM_FOCUS_OPTIONS: ExamFocusOption[] = [
  {
    id: 'learning',
    icon: '',
    label: 'Guía de aprendizaje',
    description: 'Enseña el concepto, modela un ejemplo y practica en la misma ficha',
  },
  {
    id: 'conceptual',
    icon: '',
    label: 'Guía informativa',
    description: 'Apunte de estudio: lectura, definiciones y síntesis (sin ficha pesada)',
  },
  {
    id: 'practical',
    icon: '',
    label: 'Repaso',
    description: 'Práctica breve para consolidar lo ya visto en clase',
  },
  {
    id: 'inquiry',
    icon: '',
    label: 'Cuestionario',
    description:
      'Simulación de práctica: poner a prueba lo trabajado (formativo, no calificación)',
  },
];

import type { MusicGenerationPrefs, ParsedScoreSummary } from '@interactive-room';
import type { EnfoqueDidactico } from '@interactive-room';

export type GuideRecipeMode = 'teorico' | 'aprendizaje' | 'practico' | 'descubrimiento';

export interface GuideRecipeConfig {
  includeTheory: boolean;
  includeActivities: boolean;
  guideLength?: import('@interactive-room').GuideLength;
}

export type GuideLengthId = 'short' | 'medium' | 'long';

export interface GuideLengthOption {
  id: GuideLengthId;
  icon: string;
  label: string;
  detail: string;
  blockCount: number;
}

export const GUIDE_LENGTH_OPTIONS: GuideLengthOption[] = [
  {
    id: 'short',
    icon: '',
    label: 'Corta',
    detail: '~5 bloques (ticket o repaso rápido)',
    blockCount: 5,
  },
  {
    id: 'medium',
    icon: '',
    label: 'Media',
    detail: '~8 bloques (clase / guía estándar)',
    blockCount: 8,
  },
  {
    id: 'long',
    icon: '',
    label: 'Larga',
    detail: '~12 bloques (unidad / preparación)',
    blockCount: 12,
  },
];

/** Hint de longitud según tipo de material docente. */
export function guideLengthHintForFocus(
  length: GuideLengthOption,
  focus: ExamFocusId,
  plannedBlocks: number,
): string {
  if (focus === 'conceptual') {
    return `Guía informativa · ~${plannedBlocks} bloques de estudio · ${length.detail}`;
  }
  if (focus === 'learning') {
    return `Guía de aprendizaje · ~${plannedBlocks} bloques (enseñanza + práctica) · ${length.detail}`;
  }
  if (focus === 'inquiry') {
    return `Cuestionario de práctica · ~${plannedBlocks} ítems · ${length.detail}`;
  }
  return `Repaso · ~${plannedBlocks} bloques de práctica · ${length.detail}`;
}

/**
 * Cuestionario = práctica / simulación formativa (no evaluación sumativa).
 * Reusa el id interno `inquiry` por compatibilidad de contrato.
 */
export function isQuestionnaireFocus(focus: ExamFocusId): boolean {
  return focus === 'inquiry';
}

export type QuestionnaireDelivery = 'interactive' | 'printable';

export const QUESTIONNAIRE_DELIVERY_OPTIONS: Array<{
  id: QuestionnaireDelivery;
  label: string;
  detail: string;
}> = [
  {
    id: 'interactive',
    label: 'En sala digital',
    detail: 'Misma ficha de práctica; después podés lanzarla en vivo desde el curso',
  },
  {
    id: 'printable',
    label: 'Imprimible',
    detail: 'Ficha o ticket para practicar en papel (sin nota oficial)',
  },
];

const FOCUS_TO_RECIPE_MODE: Record<ExamFocusId, GuideRecipeMode> = {
  conceptual: 'teorico',
  learning: 'aprendizaje',
  practical: 'practico',
  // Práctica simulada — no “descubrimiento/indagación” ni path de evaluación.
  inquiry: 'practico',
};

const RECIPE_DEFAULTS: Record<GuideRecipeMode, GuideRecipeConfig> = {
  teorico: { includeTheory: true, includeActivities: false, guideLength: 'medium' },
  aprendizaje: { includeTheory: true, includeActivities: true, guideLength: 'medium' },
  practico: { includeTheory: false, includeActivities: true, guideLength: 'short' },
  descubrimiento: { includeTheory: false, includeActivities: true, guideLength: 'short' },
};

export function guideRecipeModeFromFocus(focus: ExamFocusId): GuideRecipeMode {
  return FOCUS_TO_RECIPE_MODE[focus] ?? 'practico';
}

export function defaultGuideRecipeConfigForFocus(focus: ExamFocusId): GuideRecipeConfig {
  return { ...RECIPE_DEFAULTS[guideRecipeModeFromFocus(focus)] };
}

/** Dos caminos del Studio de Música — bifurca el prompt de Mistral. */
export type MusicStudioGenerationMode = 'generative' | 'repertoire';

export function resolveMusicGenerationMode(
  scoreContext?: ScoreContextPayload | null,
): MusicStudioGenerationMode {
  if (
    scoreContext?.summary?.bars &&
    Object.keys(scoreContext.summary.bars).length > 0
  ) {
    return 'repertoire';
  }
  return 'generative';
}

export interface ScoreContextPayload {
  title?: string;
  /** Telemetría estructural — verdad absoluta post-parse (no binario). */
  summary?: ParsedScoreSummary;
  /** Modo explícito — el backend también infiere desde summary. */
  generationMode?: MusicStudioGenerationMode;
  primaryPattern?: string[];
  timeSignature?: string;
  bpm?: number;
  llmBrief?: string;
  melodyNotes?: Array<{ id: string; pitch: string; label: string; duration: string }>;
}

export interface GuideKnowledgeBasePayload {
  text: string;
  fileName: string;
  charCount?: number;
  method?: string;
  truncated?: boolean;
}

export interface ExamAiBuildOptions {
  /** Tema o consigna — evaluaciones y guías sin marco curricular estructurado. */
  prompt: string;
  /** Intención y contexto de contenido — guías con marco curricular (prioridad en backend). */
  contentContext?: string;
  extension: ExamExtensionId;
  level: ExamLevelId;
  focus: ExamFocusId;
  /** Enfoque canónico en español — enviado al backend para el system prompt. */
  enfoqueDidactico?: EnfoqueDidactico;
  /** Modo de receta líquida — alias legible para el Prompt Factory. */
  mode?: GuideRecipeMode;
  /** Toggles de multiselección — teoría vs actividades. */
  config?: GuideRecipeConfig;
  resourceType?: 'exam' | 'guide';
  musicPrefs?: MusicGenerationPrefs;
  scoreContext?: ScoreContextPayload | null;
  /** Micro-RAG — material docente como única fuente de verdad. */
  knowledgeBase?: GuideKnowledgeBasePayload | null;
  /** Currículum declarado (default) vs. guía libre sin marco MINEDUC. */
  generationMode?: import('@interactive-room').GuideGenerationMode;
  /** Longitud elástica de práctica — solo cuando includeActivities está activo. */
  guideLength?: import('@interactive-room').GuideLength;
  /** OA/OAH/OAA explícitos del docente — bypass del RAG por keywords. */
  curriculumTarget?: import('@interactive-room').CurriculumTarget;
  /** Modelo 2 — receta de pantalla vs impresión. */
  deliveryFormat?: import('@interactive-room').DeliveryFormat;
  /** Perfil pedagógico — guía / clase / evaluación. */
  deliveryProfile?: import('@interactive-room').DeliveryProfile;
  /** Contexto de sesión planificada — alinea generación IA con la ruta de aprendizaje. */
  linkedSessionContext?: import('@interactive-room').LinkedSessionContext;
  /**
   * Ancla visual del docente (override) — URL Wikimedia/archivo o wikiQuery exacto.
   * Prioridad sobre referenceImage del Content Pack.
   */
  visualAnchor?: {
    imageUrl?: string;
    wikiQuery?: string;
    note?: string;
  };
}

/** Trazabilidad curricular seleccionada en el modal — para vista previa docente. */
export interface CurriculumTrace {
  axis: string;
  axisLabel: string;
  oaCode: string;
  oaText: string;
  oaSummary: string;
  oahCode?: string;
  oahText?: string;
  oaaCode?: string;
  oaaText?: string;
}

/** Texto enviado al backend como intención docente. */
export function buildGuideGenerationPrompt(contentContext: string): string {
  return String(contentContext || '').trim();
}

function resolveLevelLabel(level: string): string {
  const match = EXAM_LEVEL_OPTIONS.find((option) => option.id === level);
  return match?.label ?? level;
}

function resolveSubjectLabel(subjectTag: string, subjectLabel?: string): string {
  if (subjectLabel?.trim()) return subjectLabel.trim();
  const normalized = String(subjectTag || 'general').trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function shortenCurriculumPhrase(text: string, maxLength = 100): string {
  const trimmed = String(text || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  if (trimmed.length <= maxLength) return trimmed;
  const slice = trimmed.slice(0, maxLength);
  const lastSpace = slice.lastIndexOf(' ');
  return `${(lastSpace > 40 ? slice.slice(0, lastSpace) : slice).trim()}…`;
}

function stripCurriculumCodePrefix(text: string): string {
  return String(text || '')
    .trim()
    .replace(/^(OA|OAH|OAA)\s*[A-Za-z0-9]+:\s*/i, '')
    .trim();
}

function inferGuideTopicFromCurriculum(oaText: string, axisLabel?: string): string {
  const t = stripCurriculumCodePrefix(oaText).toLowerCase();
  if (/per[ií]metro|figura plana|geom[eé]tr|pol[ií]gono|tri[aá]ngulo|rect[aá]ngulo/.test(t)) {
    return 'perímetro y figuras planas';
  }
  if (/[aá]rea|superficie/.test(t)) return 'área y figuras planas';
  if (/fracci|decimal|porcent/.test(t)) return 'fracciones y decimales';
  if (/multiplic|divisi|[oó]n/.test(t)) return 'multiplicación y división';
  if (/n[uú]mero natural|contar|secuenc/.test(t)) return 'números y conteo';
  if (axisLabel?.trim()) return axisLabel.trim().toLowerCase();
  const words = stripCurriculumCodePrefix(oaText).split(/\s+/).slice(0, 5).join(' ');
  return words || 'el contenido del OA seleccionado';
}

function oahToStudentAction(oahText: string): string {
  const core = shortenCurriculumPhrase(stripCurriculumCodePrefix(oahText), 90);
  if (!core) return '';
  if (/^resolver problemas/i.test(core)) return 'resuelva problemas del tema con procedimiento explícito';
  if (/^formular preguntas/i.test(core)) return 'formule preguntas y respuestas frente a situaciones del tema';
  if (/^argument/i.test(core)) return 'argumente y justifique sus respuestas';
  if (/^model/i.test(core)) return 'modele situaciones con representaciones concretas y simbólicas';
  return core.charAt(0).toLowerCase() + core.slice(1);
}

function oaaToAttitudeHint(oaaText: string): string {
  const core = shortenCurriculumPhrase(stripCurriculumCodePrefix(oaaText), 80);
  if (!core) return '';
  return `Cuida que el tono invite a ${core.charAt(0).toLowerCase()}${core.slice(1)}`;
}

/**
 * Borrador de intención docente a partir del escudo curricular — sin volcar códigos OA/OAH/OAA.
 * Construye micro-planificación accionable para el textarea de generación.
 */
export function buildCurriculumIntentDraft(
  target: import('@interactive-room').CurriculumTarget,
  level: string,
  subjectTag: string,
  trace?: CurriculumTrace | null,
  subjectLabel?: string,
): string {
  const levelLabel = resolveLevelLabel(level);
  const subject = resolveSubjectLabel(subjectTag, subjectLabel);

  const oaCore = stripCurriculumCodePrefix(trace?.oaSummary || trace?.oaText || '');
  const topic = inferGuideTopicFromCurriculum(oaCore, trace?.axisLabel);
  const oaGoal = shortenCurriculumPhrase(oaCore, 120);

  const lines = [
    `Guía de ${topic} para ${levelLabel} (${subject}).`,
    oaGoal ? `Los estudiantes deben ${oaGoal.charAt(0).toLowerCase()}${oaGoal.slice(1)}.` : '',
  ];

  if (trace?.oahText) {
    lines.push(`Práctica: que ${oahToStudentAction(trace.oahText)}.`);
  }
  if (trace?.oaaText) {
    lines.push(oaaToAttitudeHint(trace.oaaText));
  }

  lines.push(
    'Estructura: contexto breve (1 bloque) → concepto clave → 3-4 ítems de práctica guiada con datos concretos → cierre reflexivo.',
  );

  if (/geom[eé]tr|per[ií]metro|[aá]rea|figura|pol[ií]gono/.test(topic + oaCore)) {
    lines.push(
      'Geometría: usa figuras dadas solo para calcular/observar; si pides dibujar, deja espacio en blanco (no entregues la figura hecha).',
    );
  }

  lines.push('Directo al grano: poca narración, consignas claras y medidas reales.');

  return lines.filter(Boolean).join(' ');
}

/** Presets de intención — espejo de guideArchetypes.js (servidor). */
export const GUIDE_INTENT_QUICK_PRESETS = [
  {
    id: 'encuesta-grupos',
    label: 'Encuesta en grupos',
    text: 'Actividad en grupos: elijan un tema, apliquen una encuesta en el curso, registren datos en tabla y grafiquen resultados.',
  },
] as const;

export function getExtensionOption(id: ExamExtensionId): ExamExtensionOption {
  return EXAM_EXTENSION_OPTIONS.find((option) => option.id === id) ?? EXAM_EXTENSION_OPTIONS[1];
}

export function getLevelOption(id: ExamLevelId): ExamLevelOption {
  return EXAM_LEVEL_OPTIONS.find((option) => option.id === id) ?? EXAM_LEVEL_OPTIONS[4];
}

export function getFocusOption(id: ExamFocusId): ExamFocusOption {
  return EXAM_FOCUS_OPTIONS.find((option) => option.id === id) ?? EXAM_FOCUS_OPTIONS.find((o) => o.id === 'learning')!;
}

